import { getCartTotal } from "@/api";
import { SkinInfo } from "@/api/decorate";
import { queryBindCheck, UserVisitInfo, visitorInfo } from "@/api/member";
import { exchangeUserRole, exchangeUserRoleDesc, queryUserInfo } from "@/api/user";
import { getGZHInfo, GZHInfo } from "@/api/wechat";
import { SubAppEventName, useSubAppMessage } from "@/utils/hooks/useSubAppMessage";
import Track from "@/utils/track";
import Cookie from "js-cookie";
import React, { useCallback } from "react";
import { createContext, useActivate } from "react-activation";
import EventManager from "../event/eventManager";
import { getUserRole } from "../identity";
import { platform } from "../platform";
import { urlQueryObject } from "../url-parse";
interface TrackProps {
  init?(config: any, superProperties: any): void;
  registerSuperProperty?(superProperties: any): void;
  alias?(user: any): void;
  pageName?(): string | undefined;
  track?(eventName: any, props?: any): void;
  pageView?(pageName: string, props?: any): void;
}

export type Role = "R" | "E" | "C" | "A";
export interface ContextProps {
  track: TrackProps;
  eventManager: EventManager;
  isExternal: boolean; // 是否是外部场景(三方 App 嵌入场景)
  userVisitInfo?: UserVisitInfo;
  reloadUserVisitInfo: () => void;
  skin: SkinInfo;
  currentRole: Role;
  gzhInfo?: GZHInfo;
  cartNum?: number;
  setCartNum: (num?: number) => Promise<number>;
  setGzhInfo: (info?: GZHInfo) => void;
  setSkin: (info: any) => void;
  setCategorySkin: (activityTagNo: string) => void;
  setActivityTagNo: (activityTagNo: string) => void;
  activityTagNo: string;
  getSkinByKey: (key: string) => any;
}
export const CommonContext = createContext<ContextProps>({
  track: {},
  eventManager: new EventManager(),
  isExternal: false,
  userVisitInfo: undefined,
  reloadUserVisitInfo: () => undefined,
  skin: {},
  currentRole: getUserRole(),
  gzhInfo: {},
  cartNum: 0,
  setCartNum: () => Promise.resolve(0),
  setGzhInfo: () => undefined,
  setSkin: () => undefined,
  setCategorySkin: () => undefined,
  setActivityTagNo: () => undefined,
  activityTagNo: "",
  getSkinByKey: () => undefined,
});

export const useCommonContext = () => React.useContext(CommonContext);

const track = Track();
const eventManager = new EventManager();
const config = {
  appkey: process.env.REACT_APP_ANALYS_APPKEY,
  debugMode: parseInt(process.env.REACT_APP_ANALYS_DEBUGMODE as any, 10),
  uploadURL: process.env.REACT_APP_ANALYS_UPLOAD_URL,
  ABkey: process.env.REACT_APP_AB_APPKEY,
};
const urlParams: any = urlQueryObject();
const msUserCode = Cookie.get("msUserCode");
const AliasCache = localStorage.getItem("ALIASCACHE");
const AliasCacheCookie = Cookie.get("ALIASCACHE");
const visitorStatus: any = Cookie.get("visitorStatus");
const visitorId = Cookie.get("visitorId");
let userId = msUserCode || AliasCache || AliasCacheCookie;
if (Number(visitorStatus) !== 0 && visitorId) {
  // 游客
  userId = visitorId;
}
const {
  shopId,
  distributorId,
  ud: shareId,
  ut: shareRole,
  t,
  channel,
  page_name,
  shortCode,
  sessionId,
} = urlParams || {};

if (!shopId || shopId === "null" || shopId === "undefined") {
  // TODO 跳到404页面/兜底逻辑
} else {
  localStorage.setItem("shopId", shopId);
}

if (!distributorId || distributorId === "null" || distributorId === "undefined") {
  localStorage.removeItem("distributorId");
} else {
  localStorage.setItem("distributorId", distributorId);
}

const superProperties = {
  $s_code: Cookie.get("shortCode") || shortCode,
  previous_page_name: page_name ? decodeURIComponent(page_name) : page_name,
  shop_id: shopId?.replace(/[#/]/g, ""), // 店铺ID
  serverid: distributorId || "null", // 服务者ID
  serverrole: distributorId ? "3" : "2", // 店长3 店主2
  shareid: shareId, // 分享者ID
  sharerole: shareRole, // 分享者角色
  t,
  source: channel,
  common_id: new Date().getTime().toString(), // 通用属性的唯一id（结合urerId作为唯一标识）
  auth_state: visitorStatus ? Number(visitorStatus) : undefined,
  userID: userId,
  user_id_source: (msUserCode && "0") || (AliasCache && "1") || (AliasCacheCookie && "2") || "-1",
  user_id: userId || "",
};
if (
  msUserCode &&
  (!(AliasCache && AliasCacheCookie) || ![AliasCache, AliasCacheCookie].includes(msUserCode))
) {
  localStorage.setItem("ALIASCACHE", msUserCode);
  Cookie.set("ALIASCACHE", msUserCode, { expires: 365 });
  track.alias(userId);
}
if (platform.isH5) {
  superProperties["client"] = "h5";
}
if (platform.isMini) {
  track.alias(urlParams.userId);
  superProperties["client"] = "miniapp";
  superProperties["$session_id"] = sessionId;
}
if (platform.isSaas) {
  track.alias(urlParams.userId);
  superProperties["client"] = "other_fm";
  superProperties["$session_id"] = sessionId;
  config.appkey = process.env.REACT_APP_SAAS_ANALYS_APPKEY;
  config.ABkey = process.env.REACT_APP_SAAS_AB_APPKEY;
}

track.init(config, superProperties);

export const CommonContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [isExternal, setExternal] = React.useState(false);
  const [activityTagNo, setActivityTagNo] = React.useState<string>("");
  const [userVisitInfo, setUserVisitInfo] = React.useState<UserVisitInfo>();
  // 首页皮肤
  const [skin, setSkin] = React.useState<any>({});
  const [gzhInfo, setGzhInfo] = React.useState<GZHInfo>();
  const [currentRole, setCurrentRole] = React.useState<Role>("C");
  const [cartNum, setTotalNum] = React.useState(0);

  const setCartNum = async (num?: number) => {
    if (typeof num === "number") {
      setTotalNum(num);
      return num;
    }
    try {
      const res = await getCartTotal({ shopBizCode: shopId });
      const { cartSkuNum = 0 } = res?.data || {};
      setTotalNum(cartSkuNum);
      return cartSkuNum;
    } catch (e) {
      console.error(e);
      setTotalNum(0);
      return 0;
    }
  };

  const reloadUserVisitInfo = () => {
    queryBindCheck().then(data => {
      setUserVisitInfo(data);
    });
  };
  // 监听子应用修改购物车数量
  useSubAppMessage(SubAppEventName.updateCartNum, setCartNum);
  // 初始化数据
  React.useEffect(() => {
    const shopId = localStorage.getItem("shopId");
    if (!shopId) {
      return;
    }
    // 初始化用户信息
    queryUserInfo().then(data => {
      const { unionId, currentRole } = data || {};
      setCurrentRole(exchangeUserRole(currentRole));

      // 获取用户信息后动态注入埋点公共参数
      if (unionId || currentRole) {
        const additionalProps = {
          unionid: unionId,
          user_category: exchangeUserRoleDesc(currentRole),
          // 后续可在此处扩展更多用户相关埋点参数
        };
        track.registerSuperProperty(additionalProps);
      }
    });
    // 初始化公众号信息
    getGZHInfo().then(data => {
      setGzhInfo(data);
    });
    // 初始化 visitorInfo
    visitorInfo().then(data => {
      // 第三方内嵌饷店场景不支持分享
      setExternal(data?.thirdLoginScene === 2);
    });
    reloadUserVisitInfo();
  }, []);

  useActivate(() => {
    reloadUserVisitInfo();
  });

  const setCategorySkin = (activityTagNo: string) => {
    try {
      let categorySkin = skin?.categorySkinVOList?.find(
        (item: any) => item.categoryCode === activityTagNo,
      );
      setSkin((prev: any) => {
        if (categorySkin) {
          const {
            categoryPageColorStart: cStart,
            categoryPageColorEnd: cEnd,
            categoryPageColor: color,
            categoryPageBgUrl: url,
          } = categorySkin;
          if (cStart && cEnd) {
            categorySkin.pageBackground = `linear-gradient(180deg, ${cStart}, ${cEnd})`;
          }
          if (color) {
            categorySkin.pageBackgroundColor = color;
          }
          if (url) {
            categorySkin.pageBackgroundImage = `url(${url})`;
          }
        }
        return { ...(prev || {}), categorySkin, navBarSkinVO: prev?.navBarSkinVO };
      });
    } catch (e) {
      console.error(e);
    }
  };

  const getSkinByKey = useCallback(
    (key: string) => {
      const { home = {}, categorySkin, navBarSkinVO, categorySkinVOList = [] } = skin;
      const transparentImg =
        "https://akim-oss.aikucun.com/32b98c6095f3c93ce3acead2f937dfbadda7e625_1745567705015_25.png";
      const { categoryBg, searchBgColor } = home || {};
      const categorySkinAll = categorySkinVOList.find(item => item.categoryCode === "10");
      if (navBarSkinVO) {
        // Tab 相关的样式属性
        switch (key) {
          case "categorySelectedTextColor":
            return navBarSkinVO.categorySelectedTextColor || home?.categorySelectedTextColor || "";
          case "categorySelectedTextLineColor":
            return (
              navBarSkinVO.categorySelectedTextLineColor ||
              home?.categorySelectedTextLineColor ||
              ""
            );
          case "categoryTextColor":
            return navBarSkinVO.categoryTextColor || home?.categoryArrowColor || "#222";
        }

        // 如果 navBarSkinVO 中有对应的样式，直接返回
        if (navBarSkinVO[key]) {
          return navBarSkinVO[key];
        }
      }

      switch (key) {
        // 首页整体背景
        case "HOME_CONTAINER": {
          let res: Record<string, any> = {};
          if (categorySkin) {
            const bgLayers: string[] = [];
            const { pageBackground, pageBackgroundColor, pageBackgroundImage } = categorySkin;
            // 1. 顶层：背景图片(如果存在)
            if (pageBackgroundImage) {
              bgLayers.push(`${pageBackgroundImage} top/100% auto no-repeat`);
            }
            // 2. 中间层：渐变背景(如果存在)
            if (pageBackground && pageBackground.includes("linear-gradient")) {
              bgLayers.push(`${pageBackground} 0 0 / 100% 425px no-repeat`);
            }
            // 3. 底层：纯色背景
            if (pageBackgroundColor) {
              bgLayers.push(pageBackgroundColor);
            } else {
              bgLayers.push("#f1f2f5");
            }

            // 如果有多层背景，合并为一个background属性
            if (bgLayers.length > 0) {
              res.background = bgLayers.join(", ");
            }
          } else if (categorySkinAll) {
            // 第一个tab的皮肤
            const bgLayers: string[] = [];
            const {
              categoryPageColorStart: cStart,
              categoryPageColorEnd: cEnd,
              categoryPageColor: color,
              categoryPageBgUrl: url,
            } = categorySkinAll;
            if (cStart && cEnd) {
              bgLayers.push(
                `linear-gradient(180deg, ${cStart}, ${cEnd}) 0 0 / 100% 425px no-repeat`,
              );
            }
            if (color) {
              bgLayers.push(color);
            } else {
              bgLayers.push("#f1f2f5");
            }
            if (url) {
              bgLayers.push(`url(${url}) top/100% auto no-repeat`);
            }
            if (bgLayers.length > 0) {
              res.background = bgLayers.join(", ");
            }
          } else {
            // 兜底
            res = {
              backgroundImage: `url(${categoryBg || transparentImg})`,
              backgroundColor: searchBgColor || "white",
            };
          }
          return res;
        }
        // 首页头部
        case "HOME_HEADER": {
          if (categorySkin || categorySkinAll) {
            return getSkinByKey("HOME_CONTAINER");
          }
          return {
            backgroundImage: `url(${categoryBg || transparentImg})`,
            backgroundColor: searchBgColor || "white",
          };
        }
        case "nagivationBtnBgColor":
        case "searchBgColor":
        case "todaySaleBg":
        case "resourceBannerBg":
        case "resourceBannerBgColor":
        case "diamondBgColor":
        case "brandsTogetherBgColor":
        case "brandsTogetherBgImg":
        case "imageBgColor": {
          if (categorySkin || categorySkinAll) {
            if (key === "searchBgColor") {
              return "";
            }
            if (["resourceBannerBg", "todaySaleBg", "brandsTogetherBgImg"].includes(key)) {
              // 透明背景图
              return transparentImg;
            }
            return "";
          }
          break;
        }
        // 导航按钮图标色
        case "nagivationIconColor": {
          // if (categorySkin) { return categorySkin?.categoryTextColor; }
          break;
        }
        case "categoryBg": {
          if (categorySkin || categorySkinAll) {
            return {};
          }
          return categoryBg ? { backgroundImage: `url(${categoryBg})` } : {};
        }
        default:
          break;
      }

      // 使用 categorySkin 中的样式
      if (categorySkin && categorySkin[key]) {
        return categorySkin[key];
      }

      // 最后使用 home 中的默认样式
      return home?.[key] || "";
    },
    [skin],
  );

  return (
    <CommonContext.Provider
      value={{
        track,
        eventManager,
        isExternal,
        userVisitInfo,
        reloadUserVisitInfo,
        skin,
        currentRole,
        gzhInfo,
        cartNum,
        setCartNum: setCartNum,
        setGzhInfo: setGzhInfo,
        setSkin,
        setCategorySkin,
        getSkinByKey,
        setActivityTagNo,
        activityTagNo,
      }}>
      {children}
    </CommonContext.Provider>
  );
};
